import { faCalendarAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useState } from 'react';
import ReactDatePicker from 'react-datepicker';
import { useTranslation } from 'react-i18next';
import { enUS, sq } from 'date-fns/locale';

const DateRange = ({
  fromDate,
  toDate,
  onChange,
  showDateMenu = true,
  disabled = false,
  dateInputStyle,
  showYearDropdown = true,
  customDateRanges = [],
  position="left",
  fromPlaceholder="Date",
  toPlaceholder="Date",
  isClearable = false,
  size,
  ...props
}) => {
  const moment = require('moment');
  const [isOpen, setIsOpen] = useState(false);
  const { t,i18n } = useTranslation("translations");
  const DateInput = React.forwardRef((props, ref) => {
    return (
      <button
        ref={ref}
        disabled={disabled}
        className={`date-range-btn${size === 'sm' ? ' date-range-btn--sm' : ''}`}
        style={{ ...dateInputStyle }}
        onClick={props.onClick}
      >
        <span className="date-range-btn__icon">
          <FontAwesomeIcon icon={faCalendarAlt} />
        </span>
        <span className="date-range-btn__text">{props.children}</span>
        <svg className="date-range-btn__arrow" width="10" height="6" viewBox="0 0 10 6" fill="none">
          <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
    );
  });


  const defaultDateRanges = [
    {
      label: t("common.today"),
      value: 'today',
      onDateChange: () => {
        let startDate = moment().startOf('day')._d;
        startDate = new Date(startDate.setHours(2, 0, 0, 0));
        let endDate = moment().endOf('day')._d;
        return [startDate, endDate];
      },
    },
    {
      label: t("common.yesterday"),
      value: 'yesterday',
      onDateChange: () => {
        let startDate = moment().subtract(1, 'days')._d;
        startDate = new Date(startDate.setHours(2, 0, 0, 0));
        let endDate = moment(startDate).endOf('day')._d;
        return [startDate, endDate];
      },
    },
    {
      label: t("common.thisWeek"),
      value: 'thisWeek',
      onDateChange: () => {
        let startDate = moment().startOf('isoWeek')._d;
        let endDate = moment().endOf('day')._d;
        return [startDate, endDate];
      },
    },
    {
      label: t("common.lastWeek"),
      value: 'lastWeek',
      onDateChange: () => {
        let startDate = moment().subtract(1, 'weeks').startOf('isoWeek')._d;
        let endDate = moment().subtract(1, 'weeks').endOf('isoWeek')._d;
        return [startDate, endDate];
      },
    },
    {
      label: t("common.thisMonth"),
      value: 'thisMonth',
      onDateChange: () => {
        let startDate = moment().startOf('month')._d;
        let endDate = moment().endOf('day')._d;
        return [startDate, endDate];
      },
    },
    {
      label: t("common.lastMonth"),
      value: 'lastMonth',
      onDateChange: () => {
        let startDate = moment().subtract(1, 'months').startOf('month')._d;
        let endDate = moment().subtract(1, 'months').endOf('month')._d;
        return [startDate, endDate];
      },
    },
  ];

  const onClickOutside = () => {
    setIsOpen(false);
    if (fromDate && !toDate) {
      onChange([fromDate, fromDate]);
    }
  };

  const combinedDateRanges = defaultDateRanges.concat(customDateRanges);

  const handleRangeClick = (onDateChange) => {
    const [startDate, endDate] = onDateChange(); 
    onChange([startDate, endDate]); 
    setIsOpen(false);
  };

  const positionClass={
    left:"date-range-actions-left",
    right:"date-range-actions-right" 
  }

  return (
    <div className="react-date-picker">
      <ReactDatePicker
        locale={i18n.language == "alb" ?sq:enUS}
        selectsRange={true}
        onInputClick={() => setIsOpen(true)}
        onClickOutside={onClickOutside}
        open={isOpen}
        showYearDropdown={showYearDropdown}
        startDate={fromDate}
        endDate={toDate}
        popperClassName="date-range-popper-high-z"
        onChange={(dates) => {
          if (dates[0] && dates[1]) {
            setIsOpen(false);
          }
          const fromDate = dates[0]
            ? typeof dates[0] === 'number'
              ? dates[0]
              : new Date(dates[0].setHours(2, 0, 0, 0))
            : null;
          const toDate = dates[1]
            ? typeof dates[1] === 'number'
              ? dates[1]
              : new Date(dates[1].setHours(2, 0, 0, 0))
            : null;
          onChange([fromDate, toDate]);
        }}
        customInput={
          <DateInput>
            {fromDate ? moment(fromDate).format('DD/MM/YYYY') : fromPlaceholder} -{' '}
            {toDate ? moment(toDate).format('DD/MM/YYYY') : toPlaceholder}
          </DateInput>
        }
        isClearable={isClearable}
        {...props}
      >
        {isOpen && showDateMenu && (
          <div className={`date-range-actions ${positionClass[position]}`}>
            {combinedDateRanges.map((range) => (
              <button
                key={range.value}
                onClick={() => handleRangeClick(range.onDateChange)}
                className="date-range-actions__item"
              >
                {range.label}
              </button>
            ))}
          </div>
        )}
      </ReactDatePicker>
    </div>
  );
};

export default DateRange;
